// Company — the administration design. One panel, because there is one
// company: the same read-only fields as before, laid out as the design's
// sections rather than a loose Bootstrap row.
//
// Every field is still registered on the same react-hook-form path
// ("company.<field>") and still disabled, exactly as it was; the screen shows
// what the back office holds and changes nothing.
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import CompanyUpdate from "../../components/adminsitration/CompanyUpdate";
import LoadingUtils from "../../core/utils/LoadingUtils";
import { fetchCompanyDetail } from "../../redux/companyDetail";
import { setheadertoggle } from "../../redux/toggle";
import {
  AdminScreen,
  AxAvatar,
  AxField,
  AxRefreshIcon,
  AxSectionHead,
  AxStatusKeys,
  AxSwitchRow,
} from "../../components/adminsitration/ui";

const Company = () => {
  const { companyInfolist, loading } = useSelector((state) => state.companyInfo);
  const { togglehead } = useSelector((state) => state.headerToggle);
  const dispatch = useDispatch();

  const {
    register,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    mode: "onChange",
    reValidateMode: "onChange",
  });

  useEffect(() => {
    setValue("company", companyInfolist[0]);
  }, [companyInfolist]);

  const handletogglehead = () => {
    dispatch(setheadertoggle(!togglehead));
  };

  const name = watch("company.name") || companyInfolist?.[0]?.name || "";
  const fullName = watch("company.fullName") || "";

  return (
    <>
      <LoadingUtils loading={loading} />
      <AdminScreen
        title="Company"
        subtitle="What every branch, invoice and report is issued under"
        tools={
          <>
            <button
              type="button"
              className="axa-iconbtn"
              title="Reload"
              onClick={() => dispatch(fetchCompanyDetail())}
            >
              <AxRefreshIcon />
            </button>
            <button
              type="button"
              className="axa-iconbtn"
              aria-pressed={!!togglehead}
              title="Collapse the app header"
              onClick={handletogglehead}
            >
              <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6">
                <path d="M4 10l4-4 4 4" />
              </svg>
            </button>
          </>
        }
        detail={
          <div className="axa-detailform">
            <div className="axa-dhead">
              <AxAvatar name={name || "company"} />
              <div className="axa-dtitle">
                <div className="t">
                  <span className="nm">{name || "Company"}</span>
                </div>
                <div className="s">{fullName || "—"}</div>
              </div>
            </div>

            <div className="axa-tabbody">
              <AxSectionHead>Identity</AxSectionHead>
              <div className="axa-formgrid">
                <AxField label="Name">
                  <input type="text" {...register("company.name")} disabled />
                </AxField>
                <AxField label="Full name">
                  <input type="text" {...register("company.fullName")} disabled />
                </AxField>
                <AxSwitchRow
                  label="Multi branch enabled"
                  note="Set when the licence was issued — it decides whether branches exist at all."
                  disabled
                  {...register("company.isMultiBranchEnabled")}
                />
              </div>

              <AxSectionHead>Address</AxSectionHead>
              <div className="axa-formgrid">
                <AxField label="Address 1">
                  <input type="text" {...register("company.address1")} disabled />
                </AxField>
                <AxField label="Address 2">
                  <input type="text" {...register("company.address2")} disabled />
                </AxField>
                <AxField label="Address 3">
                  <input type="text" {...register("company.address3")} disabled />
                </AxField>
                <AxField label="Location">
                  <input type="text" {...register("company.locationName")} disabled />
                </AxField>
              </div>

              <AxSectionHead>Contact</AxSectionHead>
              <div className="axa-formgrid">
                <AxField label="Phone">
                  <input type="text" className="mono" {...register("company.phone")} disabled />
                </AxField>
                <AxField
                  label="Email"
                  hint={errors?.company?.email?.type === "pattern" ? "Enter valid email" : undefined}
                  bad
                >
                  <input
                    type="text"
                    {...register("company.email", {
                      pattern:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                    })}
                    disabled
                  />
                </AxField>
                <AxField
                  label="Website"
                  hint={
                    errors?.company?.website?.type === "pattern" ? "Enter valid website" : undefined
                  }
                  bad
                >
                  <input
                    type="text"
                    {...register("company.website", {
                      pattern: /^(https?:\/\/)?(www\.)?([a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+)(\/[^\s]*)?$/,
                    })}
                    disabled
                  />
                </AxField>
              </div>

              <AxSectionHead>Tax</AxSectionHead>
              <div className="axa-formgrid">
                <AxField label="Tax no">
                  <input type="text" className="mono" {...register("company.tin")} disabled />
                </AxField>
                <AxField label="Sales tax no">
                  <input type="text" className="mono" {...register("company.cst")} disabled />
                </AxField>
              </div>

              <div className="axa-emptyline">
                These details come with the licence. To change them, the back office updates the
                company record and this screen follows.
              </div>
            </div>
          </div>
        }
        status={
          <AxStatusKeys
            keys={[{ k: "Read only", t: "Maintained in the back office" }]}
            right={companyInfolist?.length ? "Company loaded" : "No company returned"}
          />
        }
      >
        <CompanyUpdate />
      </AdminScreen>
    </>
  );
};

export default Company;
